import { Vue, Component } from "vue-property-decorator";
import { Getter, State } from "vuex-class";
import {
    AreaEventHistoryColumnDef,
    AreaEventHistoryColumnKey,
    AreaEventHistoryColumnPref,
    areaEventHistoryDefaultColumns
} from "@/types/sv-data/AreaEventHistory";
import { TableHeader } from "@/components/table/generic-table.vue";
import { TenantSystemPrefs } from "@/store/types";

function columnDefToTableHeader(columnDefinition: AreaEventHistoryColumnDef): TableHeader {
    const header: TableHeader = {
        title: columnDefinition.label,
        key: columnDefinition.key,
        order: columnDefinition.order,
        sortOrder: 0,
        sortOrderReversed: false,
        visible: columnDefinition.defaultVisible,
        sortable: columnDefinition.sortable,
    };

    if (columnDefinition.useCustomCell) {
        header.useCustomCell = columnDefinition.useCustomCell;
    }

    if (columnDefinition.isTermLabel) {
        header.isTermLabel = columnDefinition.isTermLabel;
    }

    if (columnDefinition.style) {
        header.style = columnDefinition.style;
    }

    return header;
}

export function buildAvailableColumns(): TableHeader[] {
    const columns = areaEventHistoryDefaultColumns.map(columnDefToTableHeader);
    return columns;
}

function isValidAreaEventHistoryColumn(obj: unknown): obj is AreaEventHistoryColumnPref {
    try {
        if (!obj || typeof obj !== "object") {
            return false;
        }
        const pref = obj as Record<string, unknown>;
        return (
            typeof pref.key === "string" &&
            Object.values(AreaEventHistoryColumnKey).includes(pref.key as AreaEventHistoryColumnKey) &&
            typeof pref.visible === "boolean"
        );
    } catch (error) {
        console.error("[AreaEventHistory] Failed to validate column preference", { error, obj });
        return false;
    }
}

export function parseEventAreaHistoryColumnPrefs(prefsString: string): AreaEventHistoryColumnPref[] {
    try {
        const parsed: unknown = JSON.parse(prefsString);
        if (!Array.isArray(parsed)) {
            return [];
        }
        const valid = parsed.filter(isValidAreaEventHistoryColumn);
        if (valid.length === 0) {
            return [];
        }
        return valid;
    } catch (error) {
        console.error("[AreaEventHistory] Failed to parse column preferences", { error, prefsString });
        return [];
    }
}

@Component({})
export default class AreaEventHistoryColumnsMixin extends Vue {
    @Getter("getFeature") public getFeature: (featureName: string[]) => boolean;
    @State("tenantSystemPrefs") public tenantSystemPrefs: TenantSystemPrefs;
	@Getter("getAreaEventHistoryDefaultColumnsConfiguration") private areaEventHistoryDefaultColumnsConfiguration: string | null;

    private get configureColumnsEnabled(): boolean {
        return this.getFeature(["Areas", "AreaEventHistory", "ColumnConfiguration", "ColumnVisibility"]);
    }

    protected get columnDescriptions(): Record<AreaEventHistoryColumnKey, string> {
        return {} as Record<AreaEventHistoryColumnKey, string>;
    }

    protected mergeDefaultWithPrefConfiguredColumns(defaultColumns: TableHeader[], prefConfigurationValue: string): TableHeader[] {
        const parsedPrefs = parseEventAreaHistoryColumnPrefs(prefConfigurationValue);
        if (!parsedPrefs.length) {
            return defaultColumns;
        }

        const prefMap = new Map(parsedPrefs.map(pref => [pref.key, pref]));

        return defaultColumns.map(col => {
            const pref = prefMap.get(col.key as AreaEventHistoryColumnKey);
            if (pref !== undefined) {
                return { ...col, visible: pref.visible };
            }
            return col;
        });
    }

    public get defaultColumns(): TableHeader[] {
        const columns = buildAvailableColumns();

        const descriptions = this.columnDescriptions;
        if (Object.keys(descriptions).length === 0) {
            return columns;
        }

        return columns.map(col => {
            const description = descriptions[col.key as AreaEventHistoryColumnKey];
            if (description !== undefined) {
                return { ...col, description };
            }
            return col;
        });
    }

	public get columns(): TableHeader[] {
        if (!this.configureColumnsEnabled) {
            return this.defaultColumns;
        }

		return this.mergeDefaultWithPrefConfiguredColumns(this.defaultColumns, this.areaEventHistoryDefaultColumnsConfiguration);
	}
}
